/**
 * navigation-enhancements.js
 * Abstracta Decor — Module 3: Navigation + Mega Menu
 *
 * Adds three interaction behaviors the approved Motion & Component Design
 * Systems call for that the existing header-menu / details-disclosure.js
 * architecture (Module 1/2, untouched) doesn't yet provide:
 *
 *   1. Hover intent — opens after a brief intentional delay on mouseenter,
 *      closes after a short grace period on mouseleave, so passing the
 *      cursor over the header doesn't accidentally trigger it (Motion
 *      System §"Hover / Interaction" for the Mega Menu component).
 *   2. Escape key — closes any open menu and returns focus to its trigger.
 *   3. Click outside — closes any open menu when the click lands outside it.
 *
 * Deliberately NOT included: any new open/close animation. The existing
 * <details>/<summary> disclosure and its motion (opacity/transform via
 * component-mega-menu.css and whatever base.css already defines) are
 * reused as-is — this file only changes *when* that existing mechanism is
 * triggered, never *how* it animates, per "Do NOT invent new animation
 * behavior."
 *
 * Gated to hover-capable, fine-pointer devices for the hover-intent
 * behavior only; touch and keyboard users are entirely unaffected by it,
 * since header-menu's built-in click/focusout handling (details-
 * disclosure.js) already covers those interactions.
 */
class NavigationDisclosureEnhancements {
  constructor() {
    this.openDelay = 150; // Motion System "Small" category (150-200ms)
    this.closeDelay = 300; // Motion System "Small/Medium" grace period
    this.timers = new WeakMap();
  }

  init() {
    this.menus = Array.from(document.querySelectorAll('header-menu'));
    if (this.menus.length === 0) return;

    // Keep aria-expanded in sync with the native <details> state regardless
    // of what triggered the toggle (click, keyboard, or hover-intent below).
    this.menus.forEach((menu) => {
      const details = menu.querySelector('details');
      const summary = details && details.querySelector('summary');
      if (!details || !summary) return;
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
      });
    });

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.menus.forEach((menu) => this.bindHoverIntent(menu));
    }

    document.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocumentClick.bind(this), true);
  }

  clearTimer(details) {
    const timer = this.timers.get(details);
    if (timer) window.clearTimeout(timer);
  }

  bindHoverIntent(menu) {
    const details = menu.querySelector('details');
    if (!details) return;

    menu.addEventListener('mouseenter', () => {
      this.clearTimer(details);
      this.timers.set(
        details,
        window.setTimeout(() => {
          if (!details.open) details.open = true;
        }, this.openDelay)
      );
    });

    menu.addEventListener('mouseleave', () => {
      this.clearTimer(details);
      this.timers.set(
        details,
        window.setTimeout(() => {
          if (details.open && !menu.contains(document.activeElement) && typeof menu.close === 'function') {
            menu.close();
          }
        }, this.closeDelay)
      );
    });
  }

  openMenus() {
    return this.menus.filter((menu) => {
      const details = menu.querySelector('details');
      return details && details.open;
    });
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const open = this.openMenus();
    if (open.length === 0) return;
    open.forEach((menu) => {
      if (typeof menu.close === 'function') menu.close();
    });
    const summary = open[0].querySelector('summary');
    if (summary) summary.focus();
  }

  onDocumentClick(event) {
    this.openMenus().forEach((menu) => {
      if (!menu.contains(event.target) && typeof menu.close === 'function') {
        menu.close();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new NavigationDisclosureEnhancements().init());
} else {
  new NavigationDisclosureEnhancements().init();
}
